import React, { useEffect, useMemo, useState } from "react";

import { apiFetch } from "./api";
import ContentEditor, {
  SECTION_LABELS,
  SECTION_ORDER,
  SECTION_VISIBILITY_PATHS,
} from "./ContentEditor";
import PatchManager from "./PatchManager";
import SiteAdminManager from "./SiteAdminManager";
import { ADMIN_APP_NAME } from "./appConfig";

const VIEW_KEYS = {
  EDITOR: "editor",
  PATCHES: "patches",
  ADMINS: "admins",
};

const URL_VIEW_KEYS = new Set(Object.values(VIEW_KEYS));

function readAdminStateFromUrl() {
  if (typeof window === "undefined") {
    return {
      view: null,
      siteId: null,
      section: null,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const view = params.get("view");
  const siteId = params.get("siteId");
  const section = params.get("section");

  return {
    view: URL_VIEW_KEYS.has(view) ? view : null,
    siteId: siteId || null,
    section: SECTION_ORDER.includes(section) ? section : null,
  };
}

function syncAdminStateToUrl(view, siteId, section) {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);

  if (view) {
    url.searchParams.set("view", view);
  } else {
    url.searchParams.delete("view");
  }

  if (siteId) {
    url.searchParams.set("siteId", siteId);
  } else {
    url.searchParams.delete("siteId");
  }

  if (view === VIEW_KEYS.EDITOR && SECTION_ORDER.includes(section)) {
    url.searchParams.set("section", section);
  } else {
    url.searchParams.delete("section");
  }

  window.history.replaceState({}, "", url.toString());
}

function getSiteTitle(site) {
  return site?.display_name || site?.repo_name || "Webbplats";
}

function getGithubPagesUrl(site) {
  if (!site?.repo_owner || !site?.repo_name) {
    return "";
  }

  const owner = String(site.repo_owner).trim();
  const repo = String(site.repo_name).trim();

  if (!owner || !repo) {
    return "";
  }

  if (repo.toLowerCase() === `${owner.toLowerCase()}.github.io`) {
    return `https://${owner}.github.io/`;
  }

  return `https://${owner}.github.io/${repo}/`;
}

function DeleteSiteModal({ site, busy, error, onCancel, onConfirm }) {
  if (!site) {
    return null;
  }

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className="shell-card confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-site-title"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="eyebrow">Avpublicera GitHub Pages</p>
        <h2 id="delete-site-title">
          Är du säker på att du vill ta bort {site.repo_name}?
        </h2>
        <p>
          Detta kör{" "}
          <code>
            DELETE /repos/{site.repo_owner}/{site.repo_name}/pages
          </code>{" "}
          och gör repot unpublished.
        </p>
        {error ? <p className="feedback feedback--error">{error}</p> : null}
        <div className="confirm-modal__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Avbryt
          </button>
          <button
            type="button"
            className="button button--danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Tar bort..." : "Ta bort"}
          </button>
        </div>
      </div>
    </div>
  );
}

function WorkspaceExplorer({
  isOpen,
  sites,
  selectedSiteId,
  expandedSiteIds,
  activeSection,
  sectionVisibility,
  isSuperAdmin,
  currentView,
  session,
  busySiteId,
  onClose,
  onNavigate,
  onToggleSite,
  onSelectSection,
  onRequestDelete,
  onSignOut,
}) {
  return (
    <>
      <div
        className={`workspace-explorer__backdrop ${isOpen ? "is-open" : ""}`}
        onClick={onClose}
        aria-hidden={!isOpen}
      />
      <aside className={`workspace-explorer ${isOpen ? "is-open" : ""}`}>
        <nav className="workspace-tree" aria-label="Webbplatser och sektioner">
          <div className="workspace-tree__root">{ADMIN_APP_NAME}</div>
          {sites.map((site) => {
            const isSelectedSite = site.site_id === selectedSiteId;
            const isExpanded = Boolean(expandedSiteIds[site.site_id]);

            return (
              <div key={site.site_id} className="explorer-site">
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  className={`explorer-row explorer-row--folder ${isSelectedSite && currentView === VIEW_KEYS.EDITOR ? "is-selected" : ""}`}
                  onClick={() => onToggleSite(site.site_id)}
                >
                  <span className="explorer-chevron">
                    {isExpanded ? "v" : ">"}
                  </span>
                  <span className="explorer-folder-icon">
                    {isExpanded ? "[]" : "[]"}
                  </span>
                  <span className="explorer-row__text">
                    {getSiteTitle(site)}
                  </span>
                </button>
                {isExpanded ? (
                  <div className="explorer-children">
                    {SECTION_ORDER.map((sectionKey) => {
                      const isActive =
                        isSelectedSite &&
                        currentView === VIEW_KEYS.EDITOR &&
                        activeSection === sectionKey;

                      return (
                        <button
                          key={`${site.site_id}-${sectionKey}`}
                          type="button"
                          className={`explorer-row explorer-row--file ${isActive ? "is-active" : ""}`}
                          onClick={() =>
                            onSelectSection(site.site_id, sectionKey)
                          }
                        >
                          {/* <span className="explorer-file-badge">Setting</span> */}
                          <span className="explorer-row__text">
                            {SECTION_LABELS[sectionKey]}
                          </span>
                          {isSelectedSite &&
                          SECTION_VISIBILITY_PATHS[sectionKey] &&
                          sectionVisibility[sectionKey] === false ? (
                            <span className="explorer-hidden-badge">Dold</span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {isExpanded && isSuperAdmin && isSelectedSite ? (
                  <button
                    type="button"
                    className="explorer-row explorer-row--danger"
                    onClick={() => onRequestDelete(site)}
                    disabled={busySiteId === site.site_id}
                  >
                    <span className="explorer-file-badge">!</span>
                    <span>
                      {busySiteId === site.site_id
                        ? "Tar bort..."
                        : "Ta bort webbplats"}
                    </span>
                  </button>
                ) : null}
              </div>
            );
          })}
        </nav>

        <div className="workspace-explorer__commands">
          {isSuperAdmin ? (
            <button
              type="button"
              className={`explorer-row explorer-row--command ${currentView === VIEW_KEYS.PATCHES ? "is-active" : ""}`}
              onClick={() => onNavigate(VIEW_KEYS.PATCHES)}
            >
              Versionshantering
            </button>
          ) : null}

          {isSuperAdmin ? (
            <button
              type="button"
              className={`explorer-row explorer-row--command ${currentView === VIEW_KEYS.ADMINS ? "is-active" : ""}`}
              onClick={() => onNavigate(VIEW_KEYS.ADMINS)}
            >
              Administratörskonton
            </button>
          ) : null}
        </div>

        <div className="workspace-explorer__footer">
          <div className="workspace-explorer__user">{session.user.email}</div>
          <button
            type="button"
            className="button button--ghost button--small"
            onClick={onSignOut}
          >
            Logga ut
          </button>
        </div>
      </aside>
    </>
  );
}

export default function AdminApp({ session, onSignOut }) {
  const initialUrlState = useMemo(() => readAdminStateFromUrl(), []);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(initialUrlState.siteId);
  const [expandedSiteIds, setExpandedSiteIds] = useState(() =>
    initialUrlState.siteId ? { [initialUrlState.siteId]: true } : {},
  );
  const [activeSection, setActiveSection] = useState(
    initialUrlState.section || SECTION_ORDER[0],
  );
  const [sectionVisibility, setSectionVisibility] = useState({});
  const [loadingSites, setLoadingSites] = useState(true);
  const [error, setError] = useState("");
  const [sitePendingDelete, setSitePendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleteBusySiteId, setDeleteBusySiteId] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentView, setCurrentView] = useState(
    initialUrlState.view || VIEW_KEYS.EDITOR,
  );

  useEffect(() => {
    let active = true;
    setLoadingSites(true);
    setError("");

    apiFetch("/me/sites", { accessToken: session.access_token })
      .then((payload) => {
        if (!active) {
          return;
        }

        const nextSites = payload.data || [];
        const nextIsSuperAdmin = Boolean(payload.meta?.isSuperAdmin);
        setSites(nextSites);
        setIsSuperAdmin(nextIsSuperAdmin);

        const requestedSite = initialUrlState.siteId
          ? nextSites.find((site) => site.site_id === initialUrlState.siteId)
          : null;
        const defaultSite =
          requestedSite || (nextSites.length ? nextSites[0] : null);
        const requestedView = initialUrlState.view;

        if (defaultSite) {
          setSelectedSiteId(defaultSite.site_id);
          setExpandedSiteIds((currentExpandedSiteIds) => ({
            ...currentExpandedSiteIds,
            [defaultSite.site_id]: true,
          }));
        }

        if (requestedView === VIEW_KEYS.PATCHES && nextIsSuperAdmin) {
          setCurrentView(VIEW_KEYS.PATCHES);
          return;
        }

        if (requestedView === VIEW_KEYS.ADMINS && nextIsSuperAdmin) {
          setCurrentView(VIEW_KEYS.ADMINS);
          return;
        }

        if (defaultSite) {
          setCurrentView(VIEW_KEYS.EDITOR);
        }
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError.message || "Kunde inte hämta webbplatser.");
        }
      })
      .finally(() => {
        if (active) {
          setLoadingSites(false);
        }
      });

    return () => {
      active = false;
    };
  }, [initialUrlState.siteId, initialUrlState.view, session.access_token]);

  useEffect(() => {
    if (loadingSites) {
      return;
    }

    syncAdminStateToUrl(currentView, selectedSiteId, activeSection);
  }, [activeSection, currentView, loadingSites, selectedSiteId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    function handlePopState() {
      const nextState = readAdminStateFromUrl();

      if (nextState.view) {
        setCurrentView(nextState.view);
      }

      setSelectedSiteId(nextState.siteId);

      if (nextState.section) {
        setActiveSection(nextState.section);
      }
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const selectedSite = useMemo(
    () =>
      sites.find((currentSite) => currentSite.site_id === selectedSiteId) ||
      null,
    [sites, selectedSiteId],
  );
  const selectedSiteUrl = getGithubPagesUrl(selectedSite);
  const activeViewLabel =
    currentView === VIEW_KEYS.PATCHES
      ? "Versionshantering"
      : currentView === VIEW_KEYS.ADMINS
        ? "Administratörskonton"
        : SECTION_LABELS[activeSection] || "Webbplats";

  function closeMenu() {
    setMenuOpen(false);
  }

  function handleNavigate(nextView) {
    setCurrentView(nextView);
    closeMenu();
  }

  function handleToggleSite(siteId) {
    setSelectedSiteId(siteId);
    setCurrentView(VIEW_KEYS.EDITOR);
    setExpandedSiteIds((currentExpandedSiteIds) => ({
      ...currentExpandedSiteIds,
      [siteId]: !currentExpandedSiteIds[siteId],
    }));
  }

  function handleSelectSection(siteId, sectionKey = SECTION_ORDER[0]) {
    setSelectedSiteId(siteId);
    setExpandedSiteIds((currentExpandedSiteIds) => ({
      ...currentExpandedSiteIds,
      [siteId]: true,
    }));
    setActiveSection(
      SECTION_ORDER.includes(sectionKey) ? sectionKey : SECTION_ORDER[0],
    );
    setCurrentView(VIEW_KEYS.EDITOR);
    closeMenu();
  }

  async function handleConfirmDelete() {
    if (!sitePendingDelete || !isSuperAdmin) {
      return;
    }

    setDeleteBusySiteId(sitePendingDelete.site_id);
    setDeleteError("");

    try {
      await apiFetch(`/admin/sites/${sitePendingDelete.site_id}/pages`, {
        accessToken: session.access_token,
        method: "DELETE",
      });

      setSites((currentSites) =>
        currentSites.filter(
          (site) => site.site_id !== sitePendingDelete.site_id,
        ),
      );
      setSelectedSiteId((currentSelectedSiteId) =>
        currentSelectedSiteId === sitePendingDelete.site_id
          ? null
          : currentSelectedSiteId,
      );
      setCurrentView(VIEW_KEYS.EDITOR);
      setSitePendingDelete(null);
    } catch (requestError) {
      setDeleteError(
        requestError.message || "Kunde inte avpublicera webbplatsen.",
      );
    } finally {
      setDeleteBusySiteId(null);
    }
  }

  return (
    <div className={`admin-shell ${menuOpen ? "is-explorer-open" : ""}`}>
      <header className="workbench-topbar">
        <button
          type="button"
          className="workspace-icon-button workbench-topbar__menu"
          onClick={() => setMenuOpen(true)}
          aria-label="Öppna explorer"
          aria-expanded={menuOpen}
        >
          ☰
        </button>
        <div className="workbench-title">{ADMIN_APP_NAME}</div>
        <div className="workbench-command-center">
          <span>
            {selectedSite ? getSiteTitle(selectedSite) : ADMIN_APP_NAME}
          </span>
          <span className="workbench-command-center__muted">/</span>
          <span>{activeViewLabel}</span>
        </div>
        <div className="workbench-topbar__actions">
          {selectedSiteUrl && currentView === VIEW_KEYS.EDITOR ? (
            <a
              className="button button--primary button--small"
              href={selectedSiteUrl}
              target="_blank"
              rel="noreferrer"
            >
              Besök sidan
            </a>
          ) : null}
        </div>
      </header>

      <div className="workbench">
        <WorkspaceExplorer
          isOpen={menuOpen}
          sites={sites}
          selectedSiteId={selectedSiteId}
          expandedSiteIds={expandedSiteIds}
          activeSection={activeSection}
          sectionVisibility={sectionVisibility}
          isSuperAdmin={isSuperAdmin}
          currentView={currentView}
          session={session}
          busySiteId={deleteBusySiteId}
          onClose={closeMenu}
          onNavigate={handleNavigate}
          onToggleSite={handleToggleSite}
          onSelectSection={handleSelectSection}
          onRequestDelete={(site) => {
            setDeleteError("");
            setSitePendingDelete(site);
          }}
          onSignOut={onSignOut}
        />

        <main
          className={`workbench-main ${currentView !== VIEW_KEYS.EDITOR ? "workbench-main--single" : ""}`}
        >
          {loadingSites ? (
            <div className="shell-card workbench-message">
              <p>Laddar webbplatser...</p>
            </div>
          ) : null}

          {!loadingSites && error ? (
            <div className="shell-card workbench-message">
              <p className="feedback feedback--error">{error}</p>
            </div>
          ) : null}

          {!loadingSites && !error && sites.length === 0 ? (
            <div className="empty-state shell-card workbench-message">
              <p className="eyebrow">Ingen åtkomst</p>
              <h2>Du har inte tillgång till någon webbplats än.</h2>
              <p>
                Be en administratör lägga till dig som medlem i{" "}
                <code>site_members</code> i databasen.
              </p>
            </div>
          ) : null}

          {!loadingSites &&
          !error &&
          isSuperAdmin &&
          currentView === VIEW_KEYS.ADMINS ? (
            <div className="workbench-admin-panel">
              <SiteAdminManager
                sites={sites}
                accessToken={session.access_token}
              />
            </div>
          ) : null}

          {!loadingSites &&
          !error &&
          isSuperAdmin &&
          currentView === VIEW_KEYS.PATCHES ? (
            <div className="workbench-admin-panel">
              <PatchManager sites={sites} accessToken={session.access_token} />
            </div>
          ) : null}

          {!loadingSites &&
          !error &&
          !selectedSite &&
          sites.length > 0 &&
          currentView === VIEW_KEYS.EDITOR ? (
            <div className="empty-state shell-card workbench-message">
              <p className="eyebrow">Explorer</p>
              <h2>Välj en webbplats i vänsterpanelen.</h2>
            </div>
          ) : null}

          {!loadingSites &&
          !error &&
          selectedSite &&
          currentView === VIEW_KEYS.EDITOR ? (
            <ContentEditor
              site={selectedSite}
              accessToken={session.access_token}
              activeSection={activeSection}
              onActiveSectionChange={setActiveSection}
              onSectionVisibilityChange={setSectionVisibility}
            />
          ) : null}
        </main>
      </div>

      <DeleteSiteModal
        site={isSuperAdmin ? sitePendingDelete : null}
        busy={deleteBusySiteId === sitePendingDelete?.site_id}
        error={deleteError}
        onCancel={() => {
          if (deleteBusySiteId) {
            return;
          }

          setDeleteError("");
          setSitePendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
